import { ChangeEvent, FunctionComponent, useState } from 'react'

import { css } from '@emotion/css'
import { DateTime } from 'luxon'
import { Trans, useTranslation } from 'react-i18next'
import { OrderMethod } from '@/libs/helpers/utils'
import { Button, CheckBox, Grid, SvgIcon, Text } from '@/tbui'
import PopupModalTemplate from '../PopupModalTemplate'

export interface RescheduleModalProps {
  orderMethod: OrderMethod
  onReschedule: () => void
  message: string
  selectNext?: {
    onSelectNext: () => void
    nextAvailable: DateTime
  }
}

const checkboxText = css`
  line-height: 24px;
  font-size: 16px;
`

const boldFont = css`
  font-family: Graphik-Bold;
`

const RescheduleModal: FunctionComponent<RescheduleModalProps> = ({
  orderMethod,
  onReschedule,
  selectNext,
  message,
}) => {
  const [scheduleNext, setScheduleNext] = useState<boolean>(false)
  const { t } = useTranslation()

  const onScheduleNextChanged = (e: ChangeEvent<HTMLInputElement>): void => {
    setScheduleNext(e.target.checked)
  }

  return (
    <Grid align="center" data-test="reschedule-modal" gap="24px">
      <PopupModalTemplate
        name="reschedule-modal-header"
        title={t('modal.reschedule_order.reschedule_modal_title', {
          context: orderMethod.toLowerCase(),
        })}
        message={message}
        icon={<SvgIcon fontSize="80px" name="alert" color="WARNING_ICON" />}
      />
      <Grid container gap="48px">
        {selectNext && (
          <Grid template="auto auto" gap="16px" align="center">
            <Grid justify="end">
              <CheckBox
                iconSize="24px"
                id="schedule-next"
                isSelected={scheduleNext}
                onChange={onScheduleNextChanged}
                data-test="schedule-next-checkbox"
                variant="SQUARE"
              />
            </Grid>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="schedule-next">
              <Text className={checkboxText}>
                <Trans
                  i18nKey="modal.reschedule_order.reschedule_modal_next_available"
                  components={{ 1: <span className={boldFont} /> }}
                  values={{
                    time:
                      selectNext.nextAvailable.toFormat('t').replace(/\./g, '').toUpperCase() ?? '',
                  }}
                />
              </Text>
            </label>
          </Grid>
        )}
        <Grid
          template="auto 1fr"
          align="center"
          gap="16px"
          sm={selectNext?.nextAvailable ? 2 : 1}
          xs={1}
        >
          <Button
            onClick={onReschedule}
            variant={selectNext?.nextAvailable ? 'SECONDARY' : 'PRIMARY'}
            data-test="schedule-new-time"
          >
            {t('modal.reschedule_order.reschedule_modal_open_selector_btn')}
          </Button>
          {selectNext && (
            <Button
              onClick={selectNext.onSelectNext}
              variant="PRIMARY"
              disabled={!scheduleNext}
              data-test="schedule-next-available"
            >
              {t('modal.reschedule_order.reschedule_modal_select_next_available_btn')}
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default RescheduleModal
